import React, { useState, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, Fence, Building2, HardHat,
  DoorOpen, TreePine, Shovel, CircleDot, Package, Upload, X,
  Phone, Mail, MapPin, Calendar, DollarSign, Camera, AlertTriangle,
  Shield, Star, CreditCard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { trackClick } from '../utils/analytics';
import { SiteHeader } from '../components/home/SiteHeader';
import { SiteFooter } from '../components/home/SiteFooter';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================================
// STEP DEFINITIONS
// ============================================================
const STEPS = ['service', 'details', 'options', 'contact', 'review', 'result'];

const SERVICE_CARDS = [
  { key: 'pipe_fence', label: 'Pipe Fence', icon: Fence, desc: 'Heavy-duty pipe fencing' },
  { key: 'metal_building', label: 'Metal Building / Shop', icon: Building2, desc: 'Custom metal buildings' },
  { key: 'concrete', label: 'Concrete', icon: HardHat, desc: 'Slabs, driveways, patios' },
  { key: 'custom_gate', label: 'Custom Gate / Automation', icon: DoorOpen, desc: 'Gates & gate automation' },
  { key: 'land_clearing', label: 'Land Clearing', icon: TreePine, desc: 'Brush & tree clearing' },
  { key: 'dirt_work', label: 'Dirt / Site Work', icon: Shovel, desc: 'Grading, leveling, pads' },
  { key: 'gravel', label: 'Gravel / Driveway', icon: CircleDot, desc: 'Driveways & gravel work' },
  { key: 'complete_package', label: 'Complete Property Package', icon: Package, desc: 'Combine multiple services' },
];

const TIMELINES = [
  { key: 'asap', label: 'ASAP' },
  { key: 'within_30_days', label: 'Within 30 Days' },
  { key: '1_3_months', label: '1–3 Months' },
  { key: '3_6_months', label: '3–6 Months' },
  { key: '6_plus', label: '6+ Months' },
  { key: 'researching', label: 'Just Researching' },
];

const BUDGETS = [
  { key: 'under_10k', label: 'Under $10,000' },
  { key: '10k_25k', label: '$10,000 – $25,000' },
  { key: '25k_50k', label: '$25,000 – $50,000' },
  { key: '50k_100k', label: '$50,000 – $100,000' },
  { key: '100k_plus', label: '$100,000+' },
  { key: 'not_sure', label: 'Not Sure' },
];

// ============================================================
// HELPER COMPONENTS
// ============================================================
const ProgressBar = ({ currentStep }) => {
  const labels = ['Project', 'Details', 'Options', 'Contact', 'Review', 'Estimate'];
  const idx = STEPS.indexOf(currentStep);
  return (
    <div className="w-full mb-8" data-testid="progress-bar">
      <div className="flex justify-between mb-2">
        {labels.map((l, i) => (
          <div key={l} className={`text-xs font-semibold ${i <= idx ? 'text-red-600' : 'text-zinc-400'}`}>
            {i < idx ? <Check size={14} className="inline mr-0.5" /> : null}{l}
          </div>
        ))}
      </div>
      <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden">
        <div className="h-full bg-red-600 transition-all duration-500 rounded-full" style={{ width: `${((idx + 1) / labels.length) * 100}%` }} />
      </div>
    </div>
  );
};

const SelectCard = ({ selected, onClick, children, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`border-2 rounded-xl p-4 text-left transition-all w-full ${selected ? 'border-red-600 bg-red-50 shadow-md' : 'border-zinc-200 bg-white hover:border-zinc-400'} ${className}`}
  >
    {children}
  </button>
);

const FieldLabel = ({ children, sub }) => (
  <label className="block mb-1.5">
    <span className="text-zinc-900 font-semibold text-sm">{children}</span>
    {sub && <span className="block text-zinc-500 text-xs mt-0.5">{sub}</span>}
  </label>
);

const NumField = ({ label, sub, value, onChange, placeholder, min = 0 }) => (
  <div>
    <FieldLabel sub={sub}>{label}</FieldLabel>
    <Input
      type="number"
      min={min}
      value={value || ''}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      placeholder={placeholder}
      className="bg-white border-zinc-300"
    />
  </div>
);

const YesNo = ({ label, value, onChange }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <div className="flex gap-3">
      {['Yes', 'No'].map(opt => (
        <button key={opt} type="button" onClick={() => onChange(opt === 'Yes')}
          className={`px-6 py-2.5 rounded-lg font-semibold text-sm border-2 transition-all ${value === (opt === 'Yes') ? 'border-red-600 bg-red-50 text-red-700' : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400'}`}
        >{opt}</button>
      ))}
    </div>
  </div>
);

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function InstantEstimate() {
  const navigate = useNavigate();
  const [step, setStep] = useState('service');
  const [selectedServices, setSelectedServices] = useState([]);
  const [isPackage, setIsPackage] = useState(false);
  const [serviceData, setServiceData] = useState({});
  const [contact, setContact] = useState({ first_name: '', last_name: '', phone: '', email: '', address: '', city: '', state: 'TX', zip_code: '', timeline: '', budget: '' });
  const [photos, setPhotos] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Save partial when user has contact info and navigates away
  useEffect(() => {
    const savePartial = () => {
      if (contact.phone || contact.email) {
        navigator.sendBeacon(`${API}/api/estimate/save-partial`, new Blob([JSON.stringify({
          contact, services_selected: selectedServices, partial_data: serviceData
        })], { type: 'application/json' }));
      }
    };
    window.addEventListener('beforeunload', savePartial);
    return () => window.removeEventListener('beforeunload', savePartial);
  }, [contact, selectedServices, serviceData]);

  // NOINDEX: This is a conversion tool, not a rankable content page
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, follow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);


  const updateService = useCallback((key, field, value) => {
    setServiceData(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));
  }, []);

  const toggleService = useCallback((key) => {
    if (key === 'complete_package') {
      setIsPackage(true);
      setSelectedServices([]);
      return;
    }
    setIsPackage(false);
    setSelectedServices(prev => prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]);
  }, []);

  const goNext = () => {
    const idx = STEPS.indexOf(step);
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1]);
  };
  const goBack = () => {
    const idx = STEPS.indexOf(step);
    if (idx > 0) setStep(STEPS[idx - 1]);
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(f => {
      const reader = new FileReader();
      reader.onloadend = () => setPhotos(prev => [...prev, { name: f.name, data: reader.result }]);
      reader.readAsDataURL(f);
    });
  };

  const calculateEstimate = async () => {
    setLoading(true);
    try {
      const payload = {
        services: serviceData,
        travel: { distance_miles: null },
        hoa: serviceData._hoa || false,
        is_complete_package: isPackage,
        zip_code: contact.zip_code,
      };
      const { data } = await axios.post(`${API}/api/estimate/calculate`, payload);
      setResult(data);
      trackClick('Estimate Calculated', '/instant-estimate');
      goNext();
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const submitEstimate = async (action) => {
    setLoading(true);
    try {
      const payload = {
        project: {
          services: serviceData,
          travel: { distance_miles: null },
          hoa: serviceData._hoa || false,
          is_complete_package: isPackage,
        },
        contact,
        photos: photos.map(p => p.name),
        site_visit_requested: action === 'site_visit',
        callback_requested: action === 'callback',
      };
      const { data } = await axios.post(`${API}/api/estimate/submit`, payload);
      setResult(prev => ({ ...prev, ...data }));
      setSubmitted(true);
      trackClick(action === 'site_visit' ? 'Site Visit Requested' : 'Callback Requested', '/instant-estimate');
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const validateContact = () => {
    const e = {};
    if (!contact.first_name.trim()) e.first_name = 'Required';
    if (!contact.last_name.trim()) e.last_name = 'Required';
    if (!contact.phone.trim()) e.phone = 'Required';
    if (!contact.email.trim() || !contact.email.includes('@')) e.email = 'Valid email required';
    if (!contact.zip_code.trim()) e.zip_code = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Active services to configure
  const activeServices = isPackage
    ? SERVICE_CARDS.filter(s => s.key !== 'complete_package').map(s => s.key)
    : selectedServices;

  // ============================================================
  // RENDER STEPS
  // ============================================================
  const renderServiceSelect = () => (
    <div>
      <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 mb-2">What can we help you build?</h2>
      <p className="text-zinc-500 mb-6">Select one or more services for your project.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {SERVICE_CARDS.map(s => {
          const selected = s.key === 'complete_package' ? isPackage : selectedServices.includes(s.key);
          return (
            <SelectCard key={s.key} selected={selected} onClick={() => toggleService(s.key)}>
              <s.icon size={28} className={selected ? 'text-red-600 mb-2' : 'text-zinc-400 mb-2'} />
              <h3 className="font-bold text-zinc-900 text-sm">{s.label}</h3>
              <p className="text-zinc-500 text-xs mt-1">{s.desc}</p>
              {s.key === 'complete_package' && (
                <p className="text-red-600 text-xs mt-1 font-semibold">One Contractor. One Project.</p>
              )}
            </SelectCard>
          );
        })}
      </div>
      {isPackage && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-red-800 text-sm font-semibold">Complete Property Package Selected</p>
          <p className="text-red-700 text-xs mt-1">You'll configure each service in the next steps. From raw property to ready-to-use.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {SERVICE_CARDS.filter(s => s.key !== 'complete_package').map(s => (
              <span key={s.key} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded font-medium">{s.label}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderFenceDetails = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2"><Fence size={20} className="text-red-600" /> Pipe Fence</h3>
      <NumField label="Fence Length" sub="Enter total linear feet" value={serviceData.pipe_fence?.length} onChange={v => updateService('pipe_fence', 'length', v)} placeholder="e.g. 500" />
      <div>
        <FieldLabel sub="Select pipe material">Pipe Material</FieldLabel>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <button type="button" onClick={() => updateService('pipe_fence', 'pipe_type', 'drill_pipe')}
            className={`py-3 px-3 rounded-lg text-center border-2 transition-all ${serviceData.pipe_fence?.pipe_type !== 'new_pipe' ? 'border-red-600 bg-red-50' : 'border-zinc-200 bg-white hover:border-zinc-400'}`}
            data-testid="pipe-type-drill_pipe"
          >
            <span className={`block font-bold text-sm ${serviceData.pipe_fence?.pipe_type !== 'new_pipe' ? 'text-red-700' : 'text-zinc-700'}`}>Drill Pipe</span>
            <span className="block text-xs text-zinc-500 mt-0.5">Oil field grade steel</span>
          </button>
          <button type="button" onClick={() => updateService('pipe_fence', 'pipe_type', 'new_pipe')}
            className={`py-3 px-3 rounded-lg text-center border-2 transition-all ${serviceData.pipe_fence?.pipe_type === 'new_pipe' ? 'border-red-600 bg-red-50' : 'border-zinc-200 bg-white hover:border-zinc-400'}`}
            data-testid="pipe-type-new_pipe"
          >
            <span className={`block font-bold text-sm ${serviceData.pipe_fence?.pipe_type === 'new_pipe' ? 'text-red-700' : 'text-zinc-700'}`}>New Pipe</span>
            <span className="block text-xs text-zinc-500 mt-0.5">Brand new steel pipe</span>
          </button>
        </div>
      </div>
      <div>
        <FieldLabel sub="Select the style that best fits your project">Fence Type</FieldLabel>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-1">
          {[
            { key: 'height_4', label: "4' Pipe Fence", desc: 'Standard height' },
            { key: 'height_5', label: "5' Pipe Fence", desc: 'Mid height' },
            { key: 'height_6', label: "6' Pipe Fence", desc: 'Full height' },
            { key: 'rail_3', label: '3 Rail Fence', desc: 'Open rail style' },
            { key: 'rail_4', label: '4 Rail Fence', desc: 'Classic ranch rail' },
            { key: 'rail_5', label: '5 Rail Fence', desc: 'Maximum coverage' },
          ].map(ft => (
            <button key={ft.key} type="button" onClick={() => updateService('pipe_fence', 'fence_type', ft.key)}
              className={`py-3 px-3 rounded-lg text-center border-2 transition-all ${serviceData.pipe_fence?.fence_type === ft.key ? 'border-red-600 bg-red-50' : 'border-zinc-200 bg-white hover:border-zinc-400'}`}
            >
              <span className={`block font-bold text-sm ${serviceData.pipe_fence?.fence_type === ft.key ? 'text-red-700' : 'text-zinc-700'}`}>{ft.label}</span>
              <span className="block text-xs text-zinc-500 mt-0.5">{ft.desc}</span>
            </button>
          ))}
        </div>
        <p className="text-zinc-500 text-xs mt-2">Drill pipe or new pipe top rail + steel posts + 12.5-gauge no-climb wire</p>
      </div>
    </div>
  );

  const renderFenceOptions = () => (
    <div className="space-y-5">
      <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2"><Fence size={20} className="text-red-600" /> Fence Options</h3>
      <YesNo label="Would you like your pipe fence painted?" value={serviceData.pipe_fence?.paint} onChange={v => updateService('pipe_fence', 'paint', v)} />
      <YesNo label="Add bottom slick wire?" value={serviceData.pipe_fence?.slick_wire} onChange={v => updateService('pipe_fence', 'slick_wire', v)} />
      <YesNo label="Is there an existing fence that needs to be removed?" value={serviceData.pipe_fence?.demo} onChange={v => updateService('pipe_fence', 'demo', v)} />
      {serviceData.pipe_fence?.demo && (
        <NumField label="Linear footage to remove" value={serviceData.pipe_fence?.demo_length} onChange={v => updateService('pipe_fence', 'demo_length', v)} placeholder="e.g. 200" />
      )}
      <div className="border-t pt-5">
        <FieldLabel sub="Select quantity for each gate type">Gates</FieldLabel>
        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          {[
            { key: 'walk_gates', label: 'Walk Gate' },
            { key: 'gates_12', label: "12' Pipe Gate" },
            { key: 'gates_16', label: "16' Pipe Gate" },
            { key: 'ornamental_gates', label: 'Custom Ornamental Entrance' },
          ].map(g => (
            <div key={g.key} className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-lg p-3">
              <span className="text-sm font-medium text-zinc-700">{g.label}</span>
              <Input type="number" min={0} value={serviceData.pipe_fence?.[g.key] || 0} onChange={e => updateService('pipe_fence', g.key, parseInt(e.target.value) || 0)} className="w-20 text-center bg-white border-zinc-300" />
            </div>
          ))}
        </div>
        {(serviceData.pipe_fence?.ornamental_gates || 0) > 0 && (
          <p className="text-amber-700 text-xs mt-2 bg-amber-50 p-2 rounded"><AlertTriangle size={12} className="inline mr-1" />Final pricing for ornamental gates requires design review.</p>
        )}
      </div>
      <YesNo label="Would you like gate automation?" value={serviceData.pipe_fence?.automation} onChange={v => updateService('pipe_fence', 'automation', v)} />
      {serviceData.pipe_fence?.automation && (
        <NumField label="Number of gate operators" value={serviceData.pipe_fence?.automation_operators || 1} onChange={v => updateService('pipe_fence', 'automation_operators', v)} min={1} placeholder="1" />
      )}
      <div>
        <FieldLabel>How would you describe the terrain?</FieldLabel>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-1">
          {[
            { key: 'flat', label: 'Mostly Flat' },
            { key: 'rolling', label: 'Rolling/Moderate' },
            { key: 'difficult', label: 'Difficult' },
            { key: 'rocky', label: 'Rocky/Severe' },
          ].map(t => (
            <button key={t.key} type="button" onClick={() => updateService('pipe_fence', 'terrain', t.key)}
              className={`py-2.5 px-3 rounded-lg text-xs font-semibold border-2 transition-all ${serviceData.pipe_fence?.terrain === t.key ? 'border-red-600 bg-red-50 text-red-700' : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400'}`}
            >{t.label}</button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBuildingDetails = () => (
    <div className="space-y-5">
      <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2"><Building2 size={20} className="text-red-600" /> Metal Building / Shop</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <NumField label="Building Width" sub="Feet" value={serviceData.metal_building?.width} onChange={v => updateService('metal_building', 'width', v)} placeholder="e.g. 40" />
        <NumField label="Building Length" sub="Feet" value={serviceData.metal_building?.length} onChange={v => updateService('metal_building', 'length', v)} placeholder="e.g. 60" />
      </div>
      {(serviceData.metal_building?.width > 0 && serviceData.metal_building?.length > 0) && (
        <p className="text-zinc-600 text-sm bg-zinc-50 p-2 rounded">Enclosed area: <strong>{(serviceData.metal_building.width * serviceData.metal_building.length).toLocaleString()} SF</strong></p>
      )}
      <div>
        <FieldLabel>Eave Height</FieldLabel>
        <div className="flex gap-3">
          {[12, 14, 16, 18].map(h => (
            <button key={h} type="button" onClick={() => updateService('metal_building', 'eave_height', h)}
              className={`flex-1 py-3 rounded-lg font-bold text-center border-2 transition-all ${serviceData.metal_building?.eave_height === h ? 'border-red-600 bg-red-50 text-red-700' : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400'}`}
            >{h}'</button>
          ))}
        </div>
      </div>
      <div>
        <FieldLabel sub="Select the package that best fits your project">Building Package</FieldLabel>
        <div className="space-y-3 mt-2">
          {[
            { key: 'shop', label: 'Shop Package', desc: 'Metal structure, roof, wall panels, standard openings, fabrication & erection' },
            { key: 'deluxe', label: 'Deluxe Package', desc: 'Shop + concrete slab, insulation, upgraded doors, electrical package, LED lighting, gutters' },
            { key: 'turnkey', label: 'Turnkey Package', desc: 'Deluxe + site prep, grading, expanded concrete, upgraded electrical, interior finish, full coordination' },
          ].map(p => (
            <SelectCard key={p.key} selected={serviceData.metal_building?.package === p.key} onClick={() => updateService('metal_building', 'package', p.key)}>
              <h4 className="font-bold text-sm">{p.label}</h4>
              <p className="text-xs text-zinc-500 mt-0.5">{p.desc}</p>
            </SelectCard>
          ))}
        </div>
      </div>
      {serviceData.metal_building?.package === 'shop' && (
        <YesNo label="Include concrete slab?" value={serviceData.metal_building?.include_concrete} onChange={v => updateService('metal_building', 'include_concrete', v)} />
      )}
      <YesNo label="Add a lean-to?" value={serviceData.metal_building?.lean_to} onChange={v => updateService('metal_building', 'lean_to', v)} />
      {serviceData.metal_building?.lean_to && (
        <div className="grid sm:grid-cols-2 gap-4 pl-4 border-l-2 border-red-200">
          <NumField label="Lean-To Length" sub="Feet" value={serviceData.metal_building?.lean_to_length} onChange={v => updateService('metal_building', 'lean_to_length', v)} placeholder="e.g. 20" />
          <NumField label="Lean-To Width" sub="Feet" value={serviceData.metal_building?.lean_to_width} onChange={v => updateService('metal_building', 'lean_to_width', v)} placeholder="e.g. 12" />
          <YesNo label="Concrete under lean-to?" value={serviceData.metal_building?.lean_to_concrete} onChange={v => updateService('metal_building', 'lean_to_concrete', v)} />
        </div>
      )}
    </div>
  );

  const renderConcreteDetails = () => {
    const areas = serviceData.concrete?.areas || [{ type: 'Shop slab', length: 0, width: 0 }];
    const setAreas = (newAreas) => setServiceData(prev => ({ ...prev, concrete: { ...prev.concrete, areas: newAreas } }));
    return (
      <div className="space-y-5">
        <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2"><HardHat size={20} className="text-red-600" /> Concrete</h3>
        {areas.map((area, i) => (
          <div key={i} className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-sm text-zinc-700">Area {i + 1}</span>
              {areas.length > 1 && (
                <button type="button" onClick={() => setAreas(areas.filter((_, j) => j !== i))} className="text-red-500 text-xs hover:underline">Remove</button>
              )}
            </div>
            <div>
              <FieldLabel>Type</FieldLabel>
              <div className="flex flex-wrap gap-2">
                {['Shop slab', 'Driveway', 'Patio', 'Equipment pad', 'Building addition', 'Other'].map(t => (
                  <button key={t} type="button" onClick={() => { const n = [...areas]; n[i] = { ...n[i], type: t }; setAreas(n); }}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all ${area.type === t ? 'border-red-600 bg-red-50 text-red-700 font-semibold' : 'border-zinc-200 bg-white text-zinc-600'}`}
                  >{t}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Length (ft)" value={area.length} onChange={v => { const n = [...areas]; n[i] = { ...n[i], length: v }; setAreas(n); }} />
              <NumField label="Width (ft)" value={area.width} onChange={v => { const n = [...areas]; n[i] = { ...n[i], width: v }; setAreas(n); }} />
            </div>
            {area.length > 0 && area.width > 0 && (
              <p className="text-zinc-600 text-xs">Area: <strong>{(area.length * area.width).toLocaleString()} SF</strong></p>
            )}
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => setAreas([...areas, { type: 'Other', length: 0, width: 0 }])}>
          + Add Another Area
        </Button>
      </div>
    );
  };

  const renderGateDetails = () => (
    <div className="space-y-5">
      <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2"><DoorOpen size={20} className="text-red-600" /> Custom Gate / Automation</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        {[
          { key: 'walk_gates', label: 'Walk Gate' },
          { key: 'gates_12', label: "12' Pipe Gate" },
          { key: 'gates_16', label: "16' Pipe Gate" },
          { key: 'ornamental_gates', label: 'Custom Ornamental Entrance' },
        ].map(g => (
          <div key={g.key} className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-lg p-3">
            <span className="text-sm font-medium text-zinc-700">{g.label}</span>
            <Input type="number" min={0} value={serviceData.custom_gate?.[g.key] || 0} onChange={e => updateService('custom_gate', g.key, parseInt(e.target.value) || 0)} className="w-20 text-center bg-white" />
          </div>
        ))}
      </div>
      <YesNo label="Would you like gate automation?" value={serviceData.custom_gate?.automation} onChange={v => updateService('custom_gate', 'automation', v)} />
      {serviceData.custom_gate?.automation && (
        <NumField label="Number of gate operators" value={serviceData.custom_gate?.automation_operators || 1} onChange={v => updateService('custom_gate', 'automation_operators', v)} min={1} />
      )}
    </div>
  );

  const renderClearingDetails = () => (
    <div className="space-y-5">
      <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2"><TreePine size={20} className="text-red-600" /> Land Clearing</h3>
      <NumField label="Approximate Acreage" value={serviceData.land_clearing?.acreage} onChange={v => updateService('land_clearing', 'acreage', v)} placeholder="e.g. 5" />
      <div>
        <FieldLabel>How would you describe the property?</FieldLabel>
        <div className="space-y-3 mt-2">
          {[
            { key: 'light', label: 'Light Clearing', desc: 'Grass, weeds, light brush and small saplings' },
            { key: 'medium', label: 'Medium Clearing', desc: 'Moderate brush, saplings, scattered trees and heavier vegetation' },
            { key: 'heavy', label: 'Heavy Clearing', desc: 'Dense vegetation, larger trees and substantial machine work' },
            { key: 'extreme', label: 'Extreme Clearing', desc: 'Large timber, substantial debris, severe terrain' },
          ].map(l => (
            <SelectCard key={l.key} selected={serviceData.land_clearing?.level === l.key} onClick={() => updateService('land_clearing', 'level', l.key)}>
              <h4 className="font-bold text-sm">{l.label}</h4>
              <p className="text-xs text-zinc-500">{l.desc}</p>
            </SelectCard>
          ))}
        </div>
      </div>
    </div>
  );

  const renderDirtDetails = () => (
    <div className="space-y-5">
      <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2"><Shovel size={20} className="text-red-600" /> Dirt / Site Work</h3>
      <div>
        <FieldLabel>What type of work?</FieldLabel>
        <div className="flex flex-wrap gap-2 mt-1">
          {['grading', 'leveling', 'building_pad', 'driveway', 'drainage', 'other'].map(t => (
            <button key={t} type="button" onClick={() => updateService('dirt_work', 'type', t)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all capitalize ${serviceData.dirt_work?.type === t ? 'border-red-600 bg-red-50 text-red-700 font-semibold' : 'border-zinc-200 bg-white text-zinc-600'}`}
            >{t.replace('_', ' ')}</button>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <NumField label="Length (ft)" value={serviceData.dirt_work?.length} onChange={v => updateService('dirt_work', 'length', v)} placeholder="e.g. 100" />
        <NumField label="Width (ft)" value={serviceData.dirt_work?.width} onChange={v => updateService('dirt_work', 'width', v)} placeholder="e.g. 60" />
      </div>
    </div>
  );

  const renderGravelDetails = () => (
    <div className="space-y-5">
      <h3 className="text-lg font-bold text-zinc-900 flex items-center gap-2"><CircleDot size={20} className="text-red-600" /> Gravel / Driveway</h3>
      <div>
        <FieldLabel>Project Type</FieldLabel>
        <div className="flex flex-wrap gap-2 mt-1">
          {['Driveway', 'Parking area', 'Shop approach', 'Landscaping', 'Building access', 'Other'].map(t => (
            <button key={t} type="button" onClick={() => updateService('gravel', 'project_type', t)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all ${serviceData.gravel?.project_type === t ? 'border-red-600 bg-red-50 text-red-700 font-semibold' : 'border-zinc-200 bg-white text-zinc-600'}`}
            >{t}</button>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <NumField label="Length (ft)" value={serviceData.gravel?.length} onChange={v => updateService('gravel', 'length', v)} placeholder="e.g. 60" />
        <NumField label="Width (ft)" value={serviceData.gravel?.width} onChange={v => updateService('gravel', 'width', v)} placeholder="e.g. 12" />
      </div>
      <div>
        <FieldLabel>Gravel Type</FieldLabel>
        <div className="flex gap-3 mt-1">
          {[{ key: 'standard', label: 'Standard / Flex Base' }, { key: 'premium', label: 'Premium Black Gravel' }].map(g => (
            <button key={g.key} type="button" onClick={() => updateService('gravel', 'gravel_type', g.key)}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-semibold border-2 transition-all ${serviceData.gravel?.gravel_type === g.key ? 'border-red-600 bg-red-50 text-red-700' : 'border-zinc-200 bg-white text-zinc-600'}`}
            >{g.label}</button>
          ))}
        </div>
      </div>
      <YesNo label="Does existing material need to be removed?" value={serviceData.gravel?.needs_removal} onChange={v => updateService('gravel', 'needs_removal', v)} />
      <YesNo label="Install landscape fabric/liner?" value={serviceData.gravel?.fabric} onChange={v => updateService('gravel', 'fabric', v)} />
      <YesNo label="Does the area require grading?" value={serviceData.gravel?.needs_grading} onChange={v => updateService('gravel', 'needs_grading', v)} />
      <YesNo label="Is equipment access straightforward?" value={serviceData.gravel?.easy_access} onChange={v => updateService('gravel', 'easy_access', v)} />
    </div>
  );

  const renderPropertyOptions = () => (
    <div className="space-y-5">
      <h3 className="text-lg font-bold text-zinc-900">Property & Site Conditions</h3>
      <YesNo label="Does your property require HOA or architectural approval?" value={serviceData._hoa} onChange={v => setServiceData(prev => ({ ...prev, _hoa: v }))} />
      {serviceData._hoa && (
        <p className="text-zinc-600 text-xs bg-zinc-50 p-3 rounded-lg">An HOA Documentation Preparation fee applies for preparation of project documentation. This does not guarantee HOA approval.</p>
      )}
    </div>
  );

  const renderDetails = () => {
    const renderers = {
      pipe_fence: renderFenceDetails,
      metal_building: renderBuildingDetails,
      concrete: renderConcreteDetails,
      custom_gate: renderGateDetails,
      land_clearing: renderClearingDetails,
      dirt_work: renderDirtDetails,
      gravel: renderGravelDetails,
    };
    return (
      <div className="space-y-8">
        <h2 className="text-2xl font-bold text-zinc-900">Project Details</h2>
        {activeServices.map(key => {
          const renderer = renderers[key];
          return renderer ? <div key={key} className="border-b border-zinc-200 pb-6 last:border-0">{renderer()}</div> : null;
        })}
      </div>
    );
  };

  const renderOptions = () => {
    const hasOptions = activeServices.includes('pipe_fence');
    return (
      <div className="space-y-8">
        <h2 className="text-2xl font-bold text-zinc-900">Options & Site Conditions</h2>
        {activeServices.includes('pipe_fence') && renderFenceOptions()}
        {renderPropertyOptions()}
        {!hasOptions && !serviceData._hoa && (
          <p className="text-zinc-500 text-sm">No additional options needed for your selected services. Continue to the next step.</p>
        )}
      </div>
    );
  };

  const renderContact = () => (
    <div className="space-y-5">
      <h2 className="text-2xl font-bold text-zinc-900">Your Information</h2>
      <p className="text-zinc-500 text-sm">We'll use this to prepare your preliminary estimate and follow up.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel>First Name</FieldLabel>
          <Input value={contact.first_name} onChange={e => setContact(p => ({ ...p, first_name: e.target.value }))} className={errors.first_name ? 'border-red-500' : ''} />
          {errors.first_name && <p className="text-red-500 text-xs mt-0.5">{errors.first_name}</p>}
        </div>
        <div>
          <FieldLabel>Last Name</FieldLabel>
          <Input value={contact.last_name} onChange={e => setContact(p => ({ ...p, last_name: e.target.value }))} className={errors.last_name ? 'border-red-500' : ''} />
        </div>
        <div>
          <FieldLabel>Phone</FieldLabel>
          <Input type="tel" value={contact.phone} onChange={e => setContact(p => ({ ...p, phone: e.target.value }))} placeholder="(817) 555-0123" className={errors.phone ? 'border-red-500' : ''} />
        </div>
        <div>
          <FieldLabel>Email</FieldLabel>
          <Input type="email" value={contact.email} onChange={e => setContact(p => ({ ...p, email: e.target.value }))} className={errors.email ? 'border-red-500' : ''} />
        </div>
      </div>
      <div>
        <FieldLabel>Project Street Address</FieldLabel>
        <Input value={contact.address} onChange={e => setContact(p => ({ ...p, address: e.target.value }))} placeholder="123 County Road" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <FieldLabel>City</FieldLabel>
          <Input value={contact.city} onChange={e => setContact(p => ({ ...p, city: e.target.value }))} />
        </div>
        <div>
          <FieldLabel>State</FieldLabel>
          <Input value={contact.state} onChange={e => setContact(p => ({ ...p, state: e.target.value }))} />
        </div>
        <div>
          <FieldLabel>ZIP Code</FieldLabel>
          <Input value={contact.zip_code} onChange={e => setContact(p => ({ ...p, zip_code: e.target.value }))} className={errors.zip_code ? 'border-red-500' : ''} />
        </div>
      </div>
      <div>
        <FieldLabel>When would you like to start?</FieldLabel>
        <div className="flex flex-wrap gap-2 mt-1">
          {TIMELINES.map(t => (
            <button key={t.key} type="button" onClick={() => setContact(p => ({ ...p, timeline: t.key }))}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all ${contact.timeline === t.key ? 'border-red-600 bg-red-50 text-red-700 font-semibold' : 'border-zinc-200 bg-white text-zinc-600'}`}
            >{t.label}</button>
          ))}
        </div>
      </div>
      <div>
        <FieldLabel>Approximate Project Budget</FieldLabel>
        <div className="flex flex-wrap gap-2 mt-1">
          {BUDGETS.map(b => (
            <button key={b.key} type="button" onClick={() => setContact(p => ({ ...p, budget: b.key }))}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all ${contact.budget === b.key ? 'border-red-600 bg-red-50 text-red-700 font-semibold' : 'border-zinc-200 bg-white text-zinc-600'}`}
            >{b.label}</button>
          ))}
        </div>
      </div>
      <div>
        <FieldLabel sub="Optional — helps us understand your project better">Upload Project / Property Photos</FieldLabel>
        <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-zinc-300 rounded-xl cursor-pointer hover:border-red-400 transition-colors bg-zinc-50">
          <Camera size={20} className="text-zinc-400" />
          <span className="text-sm text-zinc-500">Tap to upload photos</span>
          <input type="file" multiple accept="image/*" className="hidden" onChange={handlePhotoUpload} />
        </label>
        {photos.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {photos.map((p, i) => (
              <div key={i} className="relative">
                <img src={p.data} alt="" className="w-16 h-16 rounded-lg object-cover border" />
                <button type="button" onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center"><X size={10} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderReview = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-zinc-900">Review Your Project</h2>
      <Card className="border-zinc-200">
        <CardContent className="p-5 space-y-3">
          <h3 className="font-bold text-zinc-900">Services Selected</h3>
          <div className="flex flex-wrap gap-2">
            {activeServices.map(s => {
              const card = SERVICE_CARDS.find(c => c.key === s);
              return <span key={s} className="text-xs bg-red-50 text-red-700 px-3 py-1 rounded-full font-medium">{card?.label || s}</span>;
            })}
          </div>
          {isPackage && <p className="text-red-600 text-xs font-semibold">Complete Property Package</p>}
        </CardContent>
      </Card>
      <Card className="border-zinc-200">
        <CardContent className="p-5 space-y-2">
          <h3 className="font-bold text-zinc-900">Contact</h3>
          <p className="text-sm text-zinc-600">{contact.first_name} {contact.last_name}</p>
          <p className="text-sm text-zinc-600">{contact.phone} · {contact.email}</p>
          <p className="text-sm text-zinc-600">{contact.address}{contact.city ? `, ${contact.city}` : ''} {contact.state} {contact.zip_code}</p>
          {contact.timeline && <p className="text-sm text-zinc-600">Timeline: {TIMELINES.find(t => t.key === contact.timeline)?.label}</p>}
          {contact.budget && <p className="text-sm text-zinc-600">Budget: {BUDGETS.find(b => b.key === contact.budget)?.label}</p>}
        </CardContent>
      </Card>
      <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-5">
        <p className="text-zinc-700 text-xs leading-relaxed">
          <strong>Disclaimer:</strong> This is a preliminary estimate for planning purposes and is not a binding quote or contract. Final pricing is subject to verification of measurements, site conditions, accessibility, soil conditions, material selections, engineering requirements, permitting requirements, electrical service requirements, project scope and an onsite review. Changes in project scope or site conditions may affect final pricing.
        </p>
        <label className="flex items-center gap-2 mt-3 cursor-pointer">
          <input type="checkbox" checked={disclaimerAccepted} onChange={e => setDisclaimerAccepted(e.target.checked)} className="rounded border-zinc-300" />
          <span className="text-sm text-zinc-700 font-medium">I understand this is a preliminary estimate</span>
        </label>
      </div>
    </div>
  );

  const renderResult = () => {
    if (!result) return null;
    const svcLabels = result.services?.map(s => SERVICE_CARDS.find(c => c.key === s)?.label || s) || [];
    return (
      <div className="text-center space-y-8" data-testid="estimate-result">
        <div>
          <Shield className="mx-auto text-red-600 mb-4" size={48} />
          <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-2">Your Preliminary Project Estimate</h2>
          <p className="text-zinc-500">Based on the project information you've provided</p>
        </div>

        <div className="bg-zinc-900 text-white rounded-2xl p-8 max-w-lg mx-auto">
          <p className="text-zinc-400 text-sm font-semibold uppercase tracking-wider mb-2">Estimated Project Investment</p>
          <p className="text-4xl md:text-5xl font-bold">
            ${result.customer_range?.low?.toLocaleString()} – ${result.customer_range?.high?.toLocaleString()}
          </p>
        </div>

        {result.needs_review && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 max-w-lg mx-auto text-left">
            <p className="text-amber-800 font-bold text-sm flex items-center gap-2"><AlertTriangle size={16} />This project requires additional review</p>
            <ul className="text-amber-700 text-xs mt-2 space-y-1">
              {result.flags?.map((f, i) => <li key={i}>• {f}</li>)}
            </ul>
          </div>
        )}

        <Card className="max-w-lg mx-auto text-left border-zinc-200">
          <CardContent className="p-5">
            <h3 className="font-bold text-zinc-900 mb-3">Project Summary</h3>
            <div className="space-y-2 text-sm text-zinc-600">
              <p><strong>Services:</strong> {svcLabels.join(', ')}</p>
              <p><strong>Location:</strong> {contact.city || 'N/A'}, {contact.state} {contact.zip_code}</p>
              <p><strong>Timeline:</strong> {TIMELINES.find(t => t.key === contact.timeline)?.label || 'Not specified'}</p>
              {isPackage && <p className="text-red-600 font-semibold">Complete Property Package</p>}
            </div>
          </CardContent>
        </Card>

        {!submitted ? (
          <div className="space-y-4 max-w-md mx-auto">
            <h3 className="text-xl font-bold text-zinc-900">Ready for an Exact Quote?</h3>
            <Button onClick={() => submitEstimate('site_visit')} size="lg" className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-lg py-6" disabled={loading} data-testid="schedule-site-visit">
              {loading ? 'Submitting...' : 'SCHEDULE MY FREE SITE VISIT'}
            </Button>
            <Button onClick={() => submitEstimate('callback')} size="lg" variant="outline" className="w-full border-2 border-zinc-900 text-zinc-900 font-bold text-lg py-6 hover:bg-zinc-100" disabled={loading} data-testid="request-callback">
              REQUEST A CALLBACK
            </Button>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-8 max-w-lg mx-auto">
            <Check className="mx-auto text-green-600 mb-3" size={40} />
            <h3 className="text-xl font-bold text-green-800 mb-2">We've Received Your Project!</h3>
            <p className="text-green-700 text-sm">Our team will reach out shortly. Thank you for choosing North Texas Welding & Fabrication.</p>
            <Button onClick={() => navigate('/')} className="mt-4 bg-zinc-900 text-white">Return to Home</Button>
          </div>
        )}

        {/* Financing Option */}
        <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-6 max-w-lg mx-auto text-center" data-testid="estimate-financing-cta">
          <CreditCard className="mx-auto text-red-600 mb-3" size={28} />
          <h3 className="text-lg font-bold text-zinc-900 mb-2">Interested in Financing?</h3>
          <p className="text-zinc-600 text-sm mb-4">Explore financing options for your North Texas Welding and Fabrication project.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => { trackClick('financing_from_instant_estimate', '/instant-estimate'); window.open('https://your.acornfinance.com/calculator?d=U47VA&utm_source=mobile-app-copy', '_blank', 'noopener,noreferrer'); }}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold text-sm"
              data-testid="estimate-financing-acorn"
            >
              CHECK FINANCING OPTIONS
            </Button>
            <Link to="/financing" className="flex-1">
              <Button variant="outline" className="w-full border-zinc-300 text-zinc-700 font-bold text-sm" data-testid="estimate-financing-learn">
                Learn More
              </Button>
            </Link>
          </div>
          <p className="text-zinc-400 text-xs mt-3">Financing provided by third-party lenders. Subject to approval.</p>
        </div>

        <p className="text-zinc-400 text-xs max-w-lg mx-auto">
          This is a preliminary estimate for planning purposes and is not a binding quote or contract.
        </p>
      </div>
    );
  };

  // ============================================================
  // NAVIGATION LOGIC
  // ============================================================
  const canProceed = () => {
    if (step === 'service') return activeServices.length > 0;
    if (step === 'contact') return validateContact();
    if (step === 'review') return disclaimerAccepted;
    return true;
  };

  const handleNext = () => {
    if (step === 'review') {
      calculateEstimate();
    } else if (step === 'contact') {
      if (validateContact()) goNext();
    } else {
      goNext();
    }
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================
  return (
    <div className="min-h-screen bg-zinc-50">
      <Helmet>
        <title>Instant Estimate | North Texas Welding & Fabrication</title>
        <meta name="description" content="Get a free preliminary estimate for pipe fencing, metal buildings, concrete, land clearing, and more across North Texas." />
      </Helmet>
      <SiteHeader />

      <main className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        {step !== 'result' && (
          <>
            <div className="text-center mb-6">
              <h1 className="text-3xl md:text-4xl font-bold text-zinc-900">Get Your Instant Preliminary Estimate</h1>
              <p className="text-zinc-500 mt-2">Planning a project? Get a preliminary price range in just a few minutes.</p>
            </div>
            <ProgressBar currentStep={step} />
          </>
        )}

        {step === 'service' && renderServiceSelect()}
        {step === 'details' && renderDetails()}
        {step === 'options' && renderOptions()}
        {step === 'contact' && renderContact()}
        {step === 'review' && renderReview()}
        {step === 'result' && renderResult()}

        {step !== 'result' && (
          <div className="flex justify-between mt-8 pt-6 border-t border-zinc-200">
            {step !== 'service' ? (
              <Button variant="outline" onClick={goBack} className="gap-2"><ArrowLeft size={16} /> Back</Button>
            ) : <div />}
            <Button
              onClick={handleNext}
              disabled={(step === 'service' && activeServices.length === 0) || (step === 'review' && !disclaimerAccepted) || loading}
              className="bg-red-600 hover:bg-red-700 text-white gap-2 font-semibold"
            >
              {loading ? 'Calculating...' : step === 'review' ? 'Get My Estimate' : 'Next'} <ArrowRight size={16} />
            </Button>
          </div>
        )}
      </main>

      {step === 'result' && <div className="pb-12" />}
      <SiteFooter />
    </div>
  );
}
